<template>
    <div class="payroll-details-container">
        <div class="payroll-loader-container" v-if="isInitialLoading">
            <div class="payroll-loader-box">
                <DoubleLoader class="payroll-loader" />
            </div>
        </div>

        <div v-else>
            <Header :employee="this.employeeData" @export-excel="exportPayrollDetails" :show-payroll-container="showPayrollContainer" />
            <div class="payroll-flex-container-main" style="padding-left: 20px; padding-right: 20px;padding-bottom: 60px;">
                <div class="payroll-flex-container"v-if="showPayrollContainer">
                    <div class="payroll-card">
                        <EmployeeInformation ref="employeeInfo" @update-employee="updateEmployee"
                            :employee="this.employeeData" :initialValues="this.initialValues" />
                        <LeavesCalculation ref="LeavesCalculation" v-if="isProcessed" @update-leaves="updateLeaves"
                            :employee="this.employeeData" :initialValues="this.initialValues" />
                    </div>
                    <div class="payroll-card">
                        <AttendanceDetails ref="attendanceDetails" @update-attendance="updateAttendance"
                            :employee="this.employeeData" :initialValues="this.initialValues" />
                    </div>
                    <div class="payroll-card">
                        <SalaryCalculation ref="salaryCalculationRef" @save-draft="handleSaveAsDraft"
                            @update-draft="handleUpdateDraft" @process-payment="handleProcessPayroll"
                            @update-salary="updateSalary" :employee="this.employeeData"
                            :key="'salary-calc-' + employeeData.employeeId + '-' + employeeData.lateDeductionDays"
                            :initialValues="this.initialValues" :paymentProcessedLoaded="this.isPaymentProcessed"
                            :draftLoaded="this.isDrafted" :isActionProcessing="this.isActionProcessing"
                            :isDraftUpdated="this.isDraftUpdated" @delete-draft="handleDeleteDraft" />
                    </div>
                </div>
                <div v-if="!showPayrollContainer"  class="no-payroll-card">
                    <p class="no-payroll-title">No Payroll Data</p>
                    <p class="no-payroll-subtext">We couldn't find any payroll records for the selected period.</p>
                </div>
            </div>

            <ReuseableModal :modelValue="isConfirmModalOpen" @update:modelValue="isConfirmModalOpen = $event"
                :title="modalTitle" submitButtonText="Yes, Confirm" @submit="confirmPayrollAction">
                <div>{{ modalMessage }}</div>
            </ReuseableModal>
        </div>
    </div>
</template>

<script>
import Header from "./Header/index.vue";
import EmployeeInformation from "./EmployeeInformation/index.vue";
import AttendanceDetails from "./AttendanceDetails/index.vue";
import SalaryCalculation from "./SalaryCalculation/index.vue";
import LeavesCalculation from "./SalaryCalculation/LeavesCalculation.vue";
import firebase from "firebase/app";
import firebaseLib from "../../../assets/config/firebase";
import "firebase/database";
import ReuseableModal from "../../UIComponents/ReuseableModal.vue";
import DoubleLoader from "src/components/UIComponents/loaders/DoubleLoader.vue";
import { createActivityPayload, formatValue, getUpdatedFields, logAdminActivity } from "../../../utils/adminLogs";
import { handleActivityNotification } from "../../../services/notificationService";

export default {
    components: {
        Header,
        EmployeeInformation,
        AttendanceDetails,
        SalaryCalculation,
        LeavesCalculation,
        ReuseableModal,
        DoubleLoader,
    },
    props: ["employee"],
    data() {
        const user = this.$store.state.auth.user;
        return {
            currentUser: user,
            isAdmin: user && user.role === "admin",
            isDeleteModalOpen: false,
            isConfirmModalOpen: false,
            isDraftProcessing: false,
            isDraftDeleting: false,
            modalTitle: '',
            modalMessage: '',
            employeeData: {},
            attendanceData: {},
            salaryData: {},
            leavesData: {},
            initialValues: {},
            isPaymentProcessed: false,
            isActionProcessing: false,
            isDrafted: false,
            isDraftUpdated: false,
            payrollIdKey: null,
            selectedMonth: null,
            selectedYear: null,
            isInitialLoading: false,
            dbConstants: null
        };
    },
    computed: {
        selectedEmployee() {
            const emp = this.$store.state.payroll.selectedEmployeePayroll || {}
            this.employeeData = emp
            return emp;
        },
        isProcessed() {
            const processedMonth = this.selectedEmployee.month;
            const juneDecPayroll = processedMonth === "June" || processedMonth === "December";
            if (juneDecPayroll) {
                return true;
            }
            const currentDate = new Date();
            const selectedMonth = currentDate.toLocaleString('default', { month: 'long' });
            if (selectedMonth === "June" || selectedMonth === "December") {
                return true;
            }
            return false;
        },
        showPayrollContainer() {
            const currentData = this.selectedEmployee || this.employeeData || this.employee || {};
            const isEmployee = currentData.status && currentData.status.toLowerCase().trim() === "processed";
            const isAdmin = this.isAdmin
            const show = (!isEmployee && isAdmin) || (isEmployee && !isAdmin) || (isEmployee && isAdmin)
            return show;
        },
    },
    methods: {
        updateEmployee(newEmployee, isPerDaySalary = false) {
            const empData = this.employeeData || {}
            empData.basicSalary = Number(newEmployee.basicSalary);
            empData.extraAllowance = Number(newEmployee.extraAllowance);
            empData.perDaySalary = Number(newEmployee.perDaySalary);

            if (isPerDaySalary) return;
            if (empData.basicSalary !== newEmployee.basicSalary || empData.extraAllowance !== newEmployee.extraAllowance) {
                const basicSalary = parseFloat(empData.basicSalary || 0);
                const extraAllowance = parseFloat(empData.extraAllowance || 0);
                const workingDays = empData.requiredWorkingDays
                const requiredWorkingDays = parseInt(workingDays, 10) || 0;

                if (requiredWorkingDays > 0 && (basicSalary !== newEmployee.basicSalary || extraAllowance !== newEmployee.extraAllowance)) {

                    const calculatedPerDaySalary = ((basicSalary + extraAllowance) / requiredWorkingDays).toFixed(2)
                    empData.perDaySalary = calculatedPerDaySalary
                }
            }
        },

        // Update the attendance data
        updateAttendance(newAttendance) {
            const empData = this.employeeData || {}

            empData.daysWorked = newAttendance.daysWorked;
            empData.leavesTaken = newAttendance.leavesTaken;
            empData.absents = newAttendance.absents;
            empData.lateArrivals = newAttendance.lateArrivals;
            empData.leaveBalance = newAttendance.leaveBalance;
            empData.leavesDeductionDays = Number(newAttendance.leavesDeductionDays)
            empData.lateDeductionDays = Number(newAttendance.lateDeductionDays)
            empData.manualLeaveDeductionDays = Number(newAttendance.manualLeaveDeductionDays)
            empData.leaveDeductionCount = Number(newAttendance.leavesDeductionDays)
            empData.leaveDeductionNote = newAttendance.leaveDeductionNote
            empData.DBLeavesTaken = Number(newAttendance.DBLeavesTaken)
            empData.absentDays = newAttendance.absentDays
            empData.totalExtraHoursDays = newAttendance.totalExtraHoursDays
            this.$nextTick(() => {
                if (this.$refs.salaryCalculationRef) {
                    this.$refs.salaryCalculationRef.handleExternalUpdate();
                }
            });

        },

        // Update the salary data
        updateSalary(newSalary) {
            const empData = this.employeeData || {}

            empData.perDaySalary = newSalary.perDaySalary;
            empData.lateDeduction = newSalary.lateDeduction;
            empData.extraAmountAdjustment = newSalary.extraAmountAdjustment;
            empData.extraAmountAdjustmentNote = newSalary.extraAmountAdjustmentNote;
            empData.taxDeduction = newSalary.taxDeduction;
            empData.bonus = newSalary.bonus;
            empData.transactionFees = newSalary.transactionFees;
            empData.finalAmount = newSalary.finalAmount;
            empData.leavesEncashmentDays = newSalary.leavesEncashmentDays;
            empData.leavesEncashmentAmount = newSalary.leavesEncashmentAmount;
            empData.annualLeavesEncashmentDays = newSalary.annualLeavesEncashmentDays;
        },

        // Update the leaves data
        updateLeaves(newLeaves) {
            const empData = this.employeeData || {}

            empData.midYearLeaves = newLeaves.midYearLeaves;
            empData.annualLeaves = newLeaves.annualLeaves;
            empData.remainingLeaves = newLeaves.remainingLeaves;
            empData.carryForwardDays = newLeaves.carryForwardDays;
            empData.updatedLeaveQuota = newLeaves.updatedLeaveQuota;
            empData.leavesEncashmentDays = newLeaves.leavesEncashmentDays;
            empData.leavesQuota = newLeaves.leavesQuota;
            empData.annualLeavesEncashmentDays = newLeaves.leavesEncashmentDays;
        },

        async submitAutoLeave(employeeData) {
            const user = this.$store.state.auth.user;

            const { 
                employeeId,
                employeeName,
                leaveDeductionCount,
                leavesTaken,
                leaveBalance,
                month,
                year,
                leaveDeductionNote,
                leavesDeductionDays,
                lateArrivals
            } = employeeData;

            const DBLeavesTaken = Number(employeeData.DBLeavesTaken) || 0;

            const deductedLeaves =
                Number(leavesDeductionDays) ||
                Number(leaveDeductionCount) ||
                0;

            const newLeavesTaken = DBLeavesTaken + deductedLeaves;
            if (deductedLeaves <= 0) return;
            const payload = {
                assignees: user.username,
                assigneeId: user.uid,
                type: "Auto-Deducted",
                noofdays: deductedLeaves,
                reason: leaveDeductionNote || `Auto-adjusted leaves for ${lateArrivals} late arrivals – ${month} - ${year}`,
                status: "Auto-Deducted",
                userId: employeeId,
                employee: employeeName,
                dates: `-`,
                leaveBalance,
                createdAt: Date.now(),
                createdBy: "Payroll",
            };

        try {
            const res = await firebaseLib.submitLeave("leaves", payload);
            console.log("✅ Auto leave submitted:", res);

            if (deductedLeaves > 0) {
                await firebaseLib.updateEmployee({ leavesTaken: newLeavesTaken }, employeeId);
                console.log("✅ Employee leavesTaken updated:", newLeavesTaken);
            }

        } catch (err) {
            console.error("❌ Failed to submit auto leave:", err);
        }
        },

        async handleProcessPayment(isDraft = false, isDeleteDraft = false) {
            if (isDeleteDraft) return await this.deleteDraftPayroll();
            if(!isDraft) this.isActionProcessing = true
            const employeeData = this.employeeData || {};

            const isMoreLeavesDeducted = Number(employeeData.leavesTaken) < Number(employeeData.leaveDeductionCount);
            const isLeaveDeductionEqualToTaken = Number(employeeData.leavesTaken) === Number(employeeData.leaveDeductionCount);

            if ((isMoreLeavesDeducted || isLeaveDeductionEqualToTaken) && !isDraft) {
                await this.submitAutoLeave(employeeData);
            }
            const employeeId = employeeData.employeeId || "N/A";
            const employeeName = employeeData.employeeName || "Unknown";
            const basicSalary = employeeData.basicSalary || 0;
            const extraAllowance = employeeData.extraAllowance || 0;
           //const workingDays = employeeData.workingDays || 22;       // there is no workingDays in employeeData but requiredWorkingDays so I chenged it   
            const workingDays = employeeData.requiredWorkingDays || 22;
            const daysWorked = employeeData.daysWorked || 0;
            const leavesTaken = employeeData.leavesTaken || 0;
            const absents = employeeData.absents || 0;
            const lateArrivals = employeeData.lateArrivals || 0;
            const leaveBalance = employeeData.leaveBalance || 0;

            const perDaySalary = employeeData.perDaySalary || (Number(basicSalary) + Number(extraAllowance)) / workingDays;
            const lateDeduction = employeeData.lateDeduction || 0;
            const extraAmountAdjustment = employeeData.extraAmountAdjustment || 0;
            const bonus = employeeData.bonus || 0;
            const transactionFees = employeeData.transactionFees || 0;
            const finalAmount = employeeData.finalAmount || 0;
            const leavesEncashment = employeeData.leavesEncashment || 0;

            const updatedSalaryDeduction = `${lateDeduction} - ${lateArrivals}`;
            const grossSalary = Number(basicSalary) + Number(extraAllowance);
            const calculatedSalary = (perDaySalary) * daysWorked;
            const obtainedLeaves = `${leavesTaken} / ${leaveBalance}`;
            const [leavesTakenParsed, leaveBalanceParsed] = obtainedLeaves.split(" / ").map(Number);

            const currentDate = new Date();
            const selectedMonth = employeeData.month;
            const selectedYear = employeeData.year;

            const payrollRef = firebase.database().ref(`payroll/${selectedYear}/${selectedMonth}`);
            const userRef = firebase.database().ref(`users/${employeeId}`);

            // Check if a record exists for the given employee, month, and year
            const snapshot = await payrollRef.orderByChild("employeeId").equalTo(employeeId).once("value");

            let existingRecordKey = null;
            let existingData = null;

            snapshot.forEach((childSnapshot) => {
                const data = childSnapshot.val();
                if (data.month === selectedMonth && data.year === selectedYear) {
                    existingRecordKey = childSnapshot.key;
                    existingData = data;
                }
            });

            const payrollId = existingData && existingData.payrollId ? existingData.payrollId : `${selectedYear}_${selectedMonth}_${firebase.database().ref().push().key}`;

            const payrollData = {
                payrollId,
                employeeId,
                employeeName,
                employeeDesignation: employeeData.employeeDesignation,
                basicSalary: Number(basicSalary),
                perDaySalary: Number(perDaySalary) || Number(grossSalary / workingDays),
                requiredWorkingDays: Number(workingDays),
                daysWorked: Number(daysWorked),
                absents: Number(absents),
                actualAbsents: Number(employeeData.actualAbsents || 0),
                lateArrivals: Number(lateArrivals),
                lateDeduction: updatedSalaryDeduction,
                obtainedLeaves,
                leavesTaken: Number(leavesTakenParsed),
                extraAllowance: Number(extraAllowance),
                extraAmountAdjustment: Number(extraAmountAdjustment),
                bonus: Number(bonus),
                transactionFees: Number(transactionFees),
                finalAmount: Number(finalAmount),
                status: isDraft ? "Draft" : "Processed",
                shift: (employeeData.shift || "Morning").toLowerCase(),
                month: selectedMonth,
                year: selectedYear,
                leavesEncashment: Number(leavesEncashment),
                remainingLeaves: Number(`${leaveBalanceParsed - leavesTakenParsed}`),
                // leavesQuota: {
                //     remainingLeaves: Number(employeeData.leavesQuota.remainingLeaves || 0),
                //     carryForwardDays: Number(employeeData.leavesQuota.carryForwardDays || 0),
                //     updatedLeaveQuota: Number(employeeData.leavesQuota.updatedLeaveQuota || 0),
                //     leavesEncashmentDays: Number(employeeData.leavesQuota.leavesEncashmentDays || 0)
                // },
                leavesEncashmentDays: Number(employeeData.leavesEncashmentDays || 0),
                leavesEncashmentAmount: Number(employeeData.leavesEncashmentAmount || 0),
                monthlyLeaves: employeeData.monthlyLeaves || [],
                totalNormalHours: Number(employeeData.totalNormalHours || 0),
                totalExtraHours: Number(employeeData.totalExtraHours || 0),
                lowHoursDays: employeeData.lowHoursDays || {},
                totalLowHours: Number(employeeData.totalLowHours || 0),
                reqWorkingHours: Number(employeeData.reqWorkingHours || 0),
                extraAmountAdjustmentNote: employeeData.extraAmountAdjustmentNote || "",
                taxDeduction: Number(employeeData.taxDeduction || 0),
                leaveDeductionNote: employeeData.leaveDeductionNote || "",
                leavesDeductionDays: Number(employeeData.leavesDeductionDays || 0),
                lateDeductionDays: Number(employeeData.lateDeductionDays || 0),
                perDayProductiveHours: Number(employeeData.perDayProductiveHours || employeeData.productiveHours || 0),
                remainingHalfDays: employeeData.remainingHalfDays || {},
                totalHalfDayHours: Number(employeeData.totalHalfDayHours || 0),
                halfDays: employeeData.halfDays || {},
                normalDaysWorked: Number(employeeData.normalDaysWorked || 0),
                newLeaveBalance: Number(employeeData.newLeaveBalance || 0),
                requiredTotalWorkingHours: Number(employeeData.requiredTotalWorkingHours || 0),
                requiredTotalWorkingDays: employeeData.requiredTotalWorkingDays || {},
                normalHoursDays: employeeData.normalHoursDays || {},
                leavesAllowed: Number(employeeData.leavesAllowed || 0),
                leavesAvailable: Number(employeeData.leavesAvailable || 0),
                DBLeavesTaken: Number(employeeData.DBLeavesTaken || 0),
                absentDays: employeeData.absentDays || null,
                totalExtraHoursDays: employeeData.totalExtraHoursDays || null,
                empTotalHours: employeeData.empTotalHours || 0,
                normalHoursDays: employeeData.normalHoursDays || null,
            };

            if (employeeData.leavesQuota) {
                payrollData.leavesQuota = employeeData.leavesQuota;
            }
            if (employeeData.midYearLeaves) {
                payrollData.midYearLeaves = employeeData.midYearLeaves;
            }
            if (employeeData.annualLeaves) {
                payrollData.annualLeaves = employeeData.annualLeaves;
            }
            // Record when the payroll was first processed (shown as the payslip's payment date).
            // Re-saving an already processed payroll keeps the original date.
            if (!isDraft) {
                payrollData.processedAt =
                    (existingData && existingData.status === "Processed" && existingData.processedAt) || Date.now();
            }

            // Carry every remaining field of the existing record through the save.
            // The object literal above only lists the fields payroll recalculates, so
            // without this a save would silently drop display-only fields that the
            // attendance/salary tooltips read (holiday details, workingDays,
            // employeeStatus, totalDaysWorked, ...) and leave them blank until reload.
            // Firebase rejects an `undefined` value anywhere in the payload, so any
            // key that is explicitly undefined is stripped before merging.
            Object.keys(employeeData).forEach((key) => {
                if (payrollData[key] === undefined && employeeData[key] !== undefined) {
                    payrollData[key] = employeeData[key];
                }
            });

           
            if (payrollData.month === "June" || payrollData.month === "December") {
                const leavesDeduction = Number(employeeData.leavesDeductionDays) || 0;
                const DBLeavesTaken = Number(employeeData.DBLeavesTaken) || 0;
                const leavesTakenUpdated = Number(employeeData.leavesTaken) || 0;

                const deductedLeaves = Math.abs(leavesDeduction - leavesTakenUpdated);

                let diff = payrollData.month === "June" ? Math.abs(DBLeavesTaken - deductedLeaves) : 0;

                payrollData.leavesQuota = {
                    remainingLeaves: Number(employeeData.leavesQuota.remainingLeaves) || 0,
                    carryForwardDays: Number(employeeData.leavesQuota.carryForwardDays) || 0,
                    updatedLeaveQuota: Number(employeeData.leavesQuota.updatedLeaveQuota) || 0,
                    leavesEncashmentDays: Number(employeeData.leavesQuota.leavesEncashmentDays) || 0
                };
            }
            const employeeUserSnapshot = await firebase.database().ref(`users/${employeeId}`).once("value");
            let employeeUser = employeeUserSnapshot.val() || {};
            const employeeEmail = employeeUser.email || "N/A";

            try {
                if (existingRecordKey) {
                    const status = existingData.status && existingData.status;
                    if (status !== "Pending") {
                        // Update existing record if status is not "Pending"
                        console.log("update payroll record", payrollData);
                        await payrollRef.child(existingRecordKey).update(payrollData);
                        console.log("✅ Payroll record updated:", existingRecordKey);
                        swal(`${isDraft ? "Draft" : "Payroll"} has been ${status === "Draft" && !isDraft ? "Processed" : "Updated"}`, "Updated", "success");

                        if (status === "Draft" && isDraft) {
                            const updatedFields = getUpdatedFields(existingData || {}, payrollData);
                            const changes = Object.entries(updatedFields)
                                .map(([key, value]) => `${key}: ${formatValue(value.old)} → ${formatValue(value.new)}`)
                                .join('\n');

                            let title = `${this.currentUser.username} updated the payroll draft for ${employeeName} for ${selectedMonth} ${selectedYear}.`;
                            let description = `${this.currentUser.email} updated the payroll draft for ${employeeName} for ${selectedMonth} ${selectedYear}.`;

                            await logAdminActivity(
                                createActivityPayload({
                                    user: this.currentUser,
                                    title,
                                    description,
                                    changes,
                                    type: "payroll"
                                })
                            );

                        }
                    } else if(status === "Draft" && isDraft){
                        console.log("update payroll record", payrollData);
                        await payrollRef.child(existingRecordKey).update(payrollData);
                        console.log("✅ Payroll record updated:", existingRecordKey);
                        swal(`${isDraft ? "Draft" : "Payroll"} has been ${status === "Draft" && !isDraft ? "Processed" : "Updated"}`, "Updated", "success");
                    }
                    else {
                        console.log("🆕 Existing record is 'Pending', creating a new one.");
                        const newPayrollRef = payrollRef.push();
                        await newPayrollRef.set(payrollData);

                        console.log("update payroll record", payrollData);
                        swal(`${isDraft ? "Draft" : "Payroll"} has been ${status === "Draft" && !isDraft ? "Processed" : "Created"}`, "Created", "success");

                        console.log("✅ New payroll record created.");

                    }
                    const updatedFields = getUpdatedFields(existingData || {}, payrollData);
                    const changes = Object.entries(updatedFields)
                        .map(([key, value]) => `${key}: ${formatValue(value.old)} → ${formatValue(value.new)}`)
                        .join('\n');
                    if (!isDraft && !isDeleteDraft) {
                        let title = `${this.currentUser.username} processed payroll for ${employeeName} for ${selectedMonth} ${selectedYear}.`;
                        let description = `${this.currentUser.email} processed payroll for ${employeeName} for ${selectedMonth} ${selectedYear}.`;

                        await logAdminActivity(
                            createActivityPayload({
                                user: this.currentUser,
                                title,
                                description,
                                changes,
                                username: employeeName,
                                type: "payroll",
                                uid: employeeId
                            })
                        );

                        await handleActivityNotification(
                            {
                                type: "payroll",
                                activityDescription: description,
                                activityOnUser: this.employeeData.employeeId,
                                activityOnUserName: employeeName,
                                activityByUserName: this.currentUser.username,
                            },
                            this.$store.state.auth,
                            this.currentUser.uid
                        );
                    }
                } else {
                    // No record exists, create a new one
                    console.log("create payroll record", payrollData);

                    await payrollRef.child(payrollId).set(payrollData);
                    const isUpdatedBasicSalary = this.initialValues.basicSalary != basicSalary;
                    const isUpdatedExtraAllowance = this.initialValues.extraAllowance != extraAllowance;

                    // Check if employee exists in User collection and update salary details if missing
                    const userSnapshot = await userRef.once("value");
                    if (userSnapshot.exists()) {
                        const userData = userSnapshot.val();
                        let updateUserData = {};

                        if (!userData.basicSalary || isUpdatedBasicSalary) {
                            updateUserData.basicSalary = basicSalary;
                        }
                        if (!userData.extraAllowance || isUpdatedExtraAllowance) {
                            updateUserData.extraAllowance = extraAllowance;
                        }


                        if (Object.keys(updateUserData).length > 0) {
                            await userRef.update(updateUserData);
                            console.log(`✅ User ${employeeId} updated with missing salary details.`);
                        }
                    } else {
                        console.log(`⚠️ Employee ${employeeId} not found in users collection.`);
                    }

                    console.log("✅ New payroll record created.");
                    this.employeeData.status = isDraft ? "Draft" : "Processed";
                    if (payrollData.processedAt) this.employeeData.processedAt = payrollData.processedAt;

                    const updatedFields = getUpdatedFields(existingData || {}, payrollData);
                        const changes = Object.entries(updatedFields)
                            .map(([key, value]) => `${key}: ${formatValue(value.old)} → ${formatValue(value.new)}`)
                            .join('\n');

                    if (isDraft && !isDeleteDraft) {
                        let title = `${this.currentUser.username} saved the payroll draft for ${employeeName} for ${selectedMonth} ${selectedYear}.`;
                        let description = `${this.currentUser.email} saved the payroll draft for ${employeeName} for ${selectedMonth} ${selectedYear}.`;

                        await logAdminActivity(
                            createActivityPayload({
                                user: this.currentUser,
                                title,
                                description,
                                changes,
                                type: "payroll"
                            })
                        );
                    } 
                    if (!isDraft && !isDeleteDraft) {
                        let title = `${this.currentUser.username} processed payroll for ${employeeName} for ${selectedMonth} ${selectedYear}.`;
                        let description = `${this.currentUser.email} processed payroll for ${employeeName} for ${selectedMonth} ${selectedYear}.`;

                        await logAdminActivity(
                            createActivityPayload({
                                user: this.currentUser,
                                title,
                                description,
                                changes,
                                username: employeeName,
                                type: "payroll",
                                uid: employeeId
                            })
                        );

                        await handleActivityNotification(
                            {
                                type: "payroll",
                                activityDescription: description,
                                activityOnUser: this.employeeData.employeeId,
                                activityOnUserName: employeeName,
                                activityByUserName: this.currentUser.username,
                            },
                            this.$store.state.auth,
                            this.currentUser.uid
                        );
                    }
                    swal(`${isDraft ? "Draft" : "Payroll"} has been Created`, "Created", "success");
                }
                const newLeavesAllowed = payrollData.leavesQuota && payrollData.leavesQuota.updatedLeaveQuota;
                if (!isDraft && (payrollData.month === "June" || payrollData.month === "December") && newLeavesAllowed) {

                    let leaveUpdateData = {};

                    if (payrollData.month === "June") {
                        leaveUpdateData.leavesAllowed = Number(newLeavesAllowed);
                        leaveUpdateData.leavesTaken = 0;
                    }

                    if (payrollData.month === "December") {
                        leaveUpdateData.leavesAvailable = Number(newLeavesAllowed);
                        leaveUpdateData.leavesAllowed = Number(newLeavesAllowed) / 2;
                        leaveUpdateData.leavesTaken = 0;
                    }

                    if (Object.keys(leaveUpdateData).length > 0) {
                        await userRef.update(leaveUpdateData);
                    }
                } else {
                  
                }

            this.$store.commit('setSelectedEmployeePayroll', payrollData)
            if (!isDraft) {
                this.isPaymentProcessed = true;
                setTimeout(() => {
                    this.isActionProcessing = false
                }, 900);
            } else {
                this.isDrafted = true;
            }
           
            } catch (error) {
                console.log("e =>", error);
                swal("Oops!", error.message, "error");
            }
        },

        async deleteDraftPayroll() {
            try {
                const { employeeId, month, year, payrollId, status } = this.employeeData || {};
                console.log("employeeId, month, year, payrollId", employeeId, month, year, payrollId)
                if (!employeeId || !month || !year) {
                    swal("Missing Info", "Required employee data is missing.", "warning");
                    return;
                }

                if (status !== "Draft") {
                    swal("Not Allowed", "Only draft payrolls can be deleted.", "info");
                    return;
                }

                const handleSuccess = () => {
                    this.isDrafted = false;
                    this.isDraftProcessing = false;
                    this.isDraftDeleting = false;
                    swal("Deleted", "Draft payroll has been deleted successfully.", "success").then(() => {
                        this.$router.push({ path: '/payroll' });
                    });
                };

                const payrollBaseRef = firebase.database().ref(`payroll/${year}/${month}`);
                const isPayrollIdAvailable = payrollId.includes(month);
                console.log("isPayrollIdAvailable", isPayrollIdAvailable)
                if (!isPayrollIdAvailable) {
                    console.log("payrollId", payrollId)
                    const snapshot = await payrollBaseRef.once("value");

                    let draftKeyToDelete = null;

                    snapshot.forEach(childSnapshot => {
                        const data = childSnapshot.val();
                        if (
                            data.employeeId === employeeId &&
                            data.status === "Draft" &&
                            data.month.toLowerCase() === month.toLowerCase() &&
                            data.year == year
                        ) {
                            draftKeyToDelete = childSnapshot.key;
                        }
                    });

                    if (draftKeyToDelete) {
                        await payrollBaseRef.child(draftKeyToDelete).remove();
                        handleSuccess();
                    } else {
                        swal("Not Found", "No draft payroll found for this employee.", "info");
                    }

                    return;
                } else {
                    const payrollRef = payrollBaseRef.child(payrollId);
                    const snapshot = await payrollRef.once("value");

                    if (snapshot.exists()) {
                        await payrollRef.remove();
                        handleSuccess();
                    } else {
                        swal("Not Found", "No draft record found at the specified path.", "info");
                    }   
                }


            } catch (error) {
                console.error("❌ Error deleting draft payroll:", error);
                swal("Error", error.message || "Failed to delete draft payroll.", "error");
            }
        },

        async handleProcessPayroll() {
            this.isDraftProcessing = false;
            this.modalTitle = "Confirm Payroll Processing";
            this.modalMessage = "Are you sure you want to process payroll for this employee?";
            this.isConfirmModalOpen = true;
        },

        async handleSaveAsDraft() {
            this.isDraftProcessing = true;
            this.modalTitle = "Confirm Save as Draft";
            this.modalMessage = "Are you sure you want to save payroll as a draft?";
            this.isConfirmModalOpen = true;
        },
        async handleUpdateDraft() {
            this.isDraftProcessing = true;
            this.modalTitle = "Confirm Update Draft";
            this.modalMessage = "Are you sure you want to update payroll draft?";
            this.isConfirmModalOpen = true;
        },
        async handleDeleteDraft() {
            this.isDraftDeleting = true;
            this.modalTitle = "Confirm Delete Draft";
            this.modalMessage = "Are you sure you want to delete payroll draft?";
            this.isConfirmModalOpen = true;
        },
        async confirmPayrollAction() {
            this.isConfirmModalOpen = false;
            await this.handleProcessPayment(this.isDraftProcessing, this.isDraftDeleting);
        },

        exportPayrollDetails() {
            // if (!this.Admin) return;
            const employeeData = this.employeeData || {};

            const payrollData = {
                "Employee ID": employeeData.employeeId || "N/A",
                "Employee Name": employeeData.employeeName || "Unknown",
                "Employee Designation": employeeData.employeeDesignation || "Not Specified",
                "Basic Salary": employeeData.basicSalary || 0,
                "Extra Allowance": employeeData.extraAllowance || 0,
                "Total Working Days": employeeData.requiredWorkingDays || 22,
                "Days Worked": employeeData.daysWorked || 0,
                "Leaves Taken": employeeData.leavesTaken || 0,
                "Absents": employeeData.absents || 0,
                "Late Arrivals": employeeData.lateArrivals || 0,
                "Leave Balance": employeeData.leaveBalance || 0,
                "Per Day Salary": employeeData.perDaySalary || 0,
                "Late Deduction": employeeData.lateDeduction || 0,
                "Late Deduction Days": employeeData.lateDeductionDays || 0,
                "Extra Amount Adjustment": employeeData.extraAmountAdjustment || 0,
                "Extra Amount Adjustment Note": employeeData.extraAmountAdjustmentNote || "N/A",
                "Bonus": employeeData.bonus || 0,
                "Transaction Fees": employeeData.transactionFees || 0,
                "Final Amount": employeeData.finalAmount || 0,
                "Leaves Encashment": employeeData.leavesEncashment || 0,
                // "Mid-Year Leaves": employeeData.midYearLeaves && employeeData.midYearLeaves.leavesAvailable || 0,
                // "Annual Leaves Allowed": employeeData.annualLeaves && employeeData.annualLeaves.leavesAllowed || 0,
                // "Annual Leaves Available": employeeData.annualLeaves && employeeData.annualLeaves.leavesAvailable || 0,
                "Month": new Date().toLocaleString("default", { month: "long" }),
                "Year": new Date().getFullYear(),
                "Status": employeeData.status || "Pending",
            };

            // Convert data to an array for Excel
            const payrollArray = [payrollData];

            // Generate Excel file
            const worksheet = XLSX.utils.json_to_sheet(payrollArray);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "Payroll Details");

            // Download the file
            XLSX.writeFile(workbook, `${employeeData.employeeName}_payroll_${payrollData.Month}_${payrollData.Year}.xlsx`);
        },
        async fetchConstantsRealTime() {
            const constants = await this.$store.dispatch('fetchConstants');
            this.$store.commit("setConstants", constants);
            this.dbConstants = constants
            return constants || {};
        },
        async createNewPayroll() {

            try {
                const company = this.$store.state.auth.company;
                
                const companyUserIds = company.users.map(function (user) {
                    return user.uid;
                });
                
                const allEmployees = this.$store.state.auth.allUsers.filter(function (user) {
                    return companyUserIds.indexOf(user.uid) !== -1;
                });
                
                const notExistingPayrollEmp = allEmployees.find((v,i) => {
                    return v.uid === (this.selectedEmployee && this.selectedEmployee.uid || this.employee && this.employee.employeeId || this.payrollIdKey);
                })

                
                const selectedYear = this.selectedYear || this.employee.year
                const selectedMonth = this.selectedMonth || this.employee.month
                const emp = notExistingPayrollEmp || this.employee

                if (!this.isAdmin && notExistingPayrollEmp) {
                    swal("Oops!", `No processed payroll found for ${selectedMonth} ${selectedYear}.`, "error");
                    return;
                }
                const constants = this.dbConstants || this.fetchConstantsRealTime()

                const newPayroll = await this.$store.dispatch('createPayrollForEmployee', {
                    emp: emp,
                    selectedYear: Number(selectedYear),
                    selectedMonth: selectedMonth,
                    constantsCollection: constants && constants.constant ? constants.constant : constants
                });
                if (newPayroll) {
                    this.$store.commit('setSelectedEmployeePayroll', newPayroll);
                } else {
                    console.warn("No new payroll created.");
                    swal("Oops!", `No processed payroll found for ${this.selectedMonth}, ${this.selectedYear}.`, "error");
                }

            } catch (error) {
                console.error("Error creating new payroll:", error);
                throw error;
            }
        },
        async fetchPayrollByIdKey() {
            if (!this.payrollIdKey || !this.selectedYear || !this.selectedMonth) return;
            // Local flag only: the shared store `loading` also drives the payroll
            // list page, and toggling it here would blank this page mid-session.
            this.isInitialLoading = true;

            try {
                const payrollRef = firebase
                    .database()
                    .ref(`payroll/${this.selectedYear}/${this.selectedMonth}/${this.payrollIdKey}`);

                const snapshot = await payrollRef.once('value');

                if (snapshot.exists()) {
                    const payrollData = snapshot.val();
                    this.$store.commit('setSelectedEmployeePayroll', payrollData);
                } else {
                    await this.createNewPayroll();
                }
            } catch (error) {
                console.error("Error fetching payroll by ID key:", error);
            } finally {
                this.isInitialLoading = false;
            }
        },
        getPayrollIdKeyFromUrl() {
            const match = window.location.hash.match(/\/payroll\/details\/([^/]+)/);
            return match ? match[1] : null;
        }
    },
    watch: {
        '$store.state.payroll.selectedEmployeePayroll': function (newVal) {
            if (newVal) {
                this.initialValues = JSON.parse(JSON.stringify(newVal));
            }
        },
    },
    async created() {

        const company = this.$store.state.auth.company;

        if (!firebase.apps.length) {
            await firebaseLib.configureFirebase(company.firebase);

            this.payrollIdKey = this.getPayrollIdKeyFromUrl();
            this.selectedMonth = localStorage.getItem("payrollMonth") || this.selectedMonth;
            this.selectedYear = localStorage.getItem("payrollYear") || this.selectedYear;

            if (this.payrollIdKey && this.selectedYear && this.selectedMonth) {
                console.log("dayaaaaa")
                await this.fetchConstantsRealTime()
                this.fetchPayrollByIdKey();
            }
        }
        const initialEmployeeData = this.$store.state.payroll.selectedEmployeePayroll || {};
        this.initialValues = JSON.parse(JSON.stringify(initialEmployeeData)); // Deep copy to prevent mutation

    },
};
</script>

<style scoped>
.payroll-loader {
    display: flex;
    justify-content: center;
    align-items: center;
}

.payroll-loader-container {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    min-height: calc(100vh - 133px);
}

.payroll-loader-box {
    width: 48px;
    height: 48px;
    display: flex;
    justify-content: center;
    align-items: center;
}
</style>



// WEBPACK FOOTER //
// src/components/PayrollManagement/PayrollDetails/index.vue